import { Component } from '@theme/component';

/**
 * Step logic for the "Find My Size" quiz. Lives inside the theme's shared
 * <dialog-component> (see blocks/size-finder.liquid), which owns open/
 * close, scroll-lock, outside-click, and animation — this component only
 * owns step navigation and the size estimate.
 *
 * The estimate is matched against the same breakpoints published in the
 * product page's size-guide table (templates/product.json's
 * size-guide-container). If that table's numbers change, update the two
 * breakpoint arrays below to match — they're intentionally not scraped
 * from the DOM so the quiz still works on templates that don't render the
 * full chart.
 *
 * The chest→waist drop used to estimate a trouser size (6" up to a 45"
 * chest, 5" above it) isn't invented: it's the exact relationship already
 * present in ANIÉZÉ's own chart (e.g. S: 36 chest / 30 waist = 6" drop;
 * 2XL: 48 chest / 43 waist = 5" drop), so the estimate stays consistent
 * with the real chart it's approximating.
 */

const SIZE_LABELS = ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];
const CHEST_BREAKPOINTS = [36, 39, 42, 45, 48, 52, 56];
const WAIST_BREAKPOINTS = [30, 33, 36, 39, 43, 47, 51];

const BODY_TYPE_ADJUSTMENT = {
  narrower: -2,
  average: 0,
  broader: 2,
};

/** Reference point the estimate is anchored to: 5'9", 160lb, average build. */
const REFERENCE_HEIGHT_IN = 69;
const REFERENCE_WEIGHT_LB = 160;
const REFERENCE_CHEST = 40;

/** @param {number} chest */
function sizeForChest(chest) {
  const index = CHEST_BREAKPOINTS.findIndex((breakpoint) => chest <= breakpoint);
  return SIZE_LABELS[index === -1 ? SIZE_LABELS.length - 1 : index];
}

/** @param {number} waist */
function sizeForWaist(waist) {
  const index = WAIST_BREAKPOINTS.findIndex((breakpoint) => waist <= breakpoint);
  return SIZE_LABELS[index === -1 ? SIZE_LABELS.length - 1 : index];
}

export class SizeFinderQuiz extends Component {
  requiredRefs = ['step-1', 'step-2', 'step-3'];

  connectedCallback() {
    super.connectedCallback();
    this.#selectBodyType('average');
    this.#showStep(1);
  }

  goToStep1() {
    this.#showStep(1);
  }

  goToStep2() {
    const feet = Number(/** @type {HTMLInputElement} */ (this.refs['height-feet']).value);
    const inches = Number(/** @type {HTMLInputElement} */ (this.refs['height-inches']).value);
    const weight = Number(/** @type {HTMLInputElement} */ (this.refs.weight).value);

    if (!feet || !weight) {
      /** @type {HTMLElement} */ (this.refs['step-1-error']).hidden = false;
      return;
    }
    /** @type {HTMLElement} */ (this.refs['step-1-error']).hidden = true;

    this.dataset.heightIn = String(feet * 12 + (inches || 0));
    this.dataset.weightLb = String(weight);
    this.#showStep(2);
  }

  /** @param {Event} event */
  selectBodyType(event) {
    const value = /** @type {HTMLElement} */ (event.target).dataset.bodyType;
    if (value) this.#selectBodyType(value);
  }

  calculate() {
    const heightIn = Number(this.dataset.heightIn);
    const weightLb = Number(this.dataset.weightLb);
    const bodyType = this.dataset.bodyType || 'average';

    const weightDelta = weightLb - REFERENCE_WEIGHT_LB;
    const heightDelta = heightIn - REFERENCE_HEIGHT_IN;

    const estimatedChest = REFERENCE_CHEST + weightDelta * 0.12 - heightDelta * 0.15 + BODY_TYPE_ADJUSTMENT[bodyType];
    const drop = estimatedChest <= 45 ? 6 : 5;
    const estimatedWaist = estimatedChest - drop;

    /** @type {HTMLElement} */ (this.refs['result-top']).textContent = sizeForChest(estimatedChest);
    /** @type {HTMLElement} */ (this.refs['result-trouser']).textContent = sizeForWaist(estimatedWaist);
    this.#showStep(3);
  }

  reset() {
    /** @type {HTMLInputElement} */ (this.refs['height-feet']).value = '';
    /** @type {HTMLInputElement} */ (this.refs['height-inches']).value = '';
    /** @type {HTMLInputElement} */ (this.refs.weight).value = '';
    this.#selectBodyType('average');
    this.#showStep(1);
  }

  /** @param {string} value */
  #selectBodyType(value) {
    this.dataset.bodyType = value;
    const options = this.refs['body-type-option'];
    if (Array.isArray(options)) {
      for (const option of options) {
        option.classList.toggle('is-selected', option.dataset.bodyType === value);
      }
    }
  }

  /** @param {1 | 2 | 3} step */
  #showStep(step) {
    /** @type {HTMLElement} */ (this.refs['step-1']).hidden = step !== 1;
    /** @type {HTMLElement} */ (this.refs['step-2']).hidden = step !== 2;
    /** @type {HTMLElement} */ (this.refs['step-3']).hidden = step !== 3;
    this.dataset.step = String(step);
  }
}

if (!customElements.get('size-finder-quiz')) {
  customElements.define('size-finder-quiz', SizeFinderQuiz);
}
